import {
  addDoc,
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  runTransaction,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore'
import { firestore } from '@core/services/firebase/firebase-service'
import { WebinarSuggestionsErrorConverter } from '@core/services/webinar-suggestions/converters/webinar-suggestions-error-converter'
import { CoreError, CoreErrorCause } from '@domain/models/core-error'
import type {
  CreateWebinarSuggestionInput,
  UpdateWebinarSuggestionPatch,
  WebinarSuggestion,
  WebinarSuggestionStatus,
} from '@domain/models/webinar-suggestions'
import type { UserProfile } from '@domain/models/user'

const SUGGESTIONS_COLLECTION = 'webinar_suggestions'
const SUGGESTION_LIKES_SUBCOLLECTION = 'likes'
const USERS_COLLECTION = 'users'
const WEBINAR_SUGGESTION_TYPE = 'webinar'
const SUBJECT_MAX_LENGTH = 80
const DESCRIPTION_MAX_LENGTH = 400
const ADMIN_REPLY_LABEL_MAX_LENGTH = 40

function toSuggestionStatus(value: unknown): WebinarSuggestionStatus {
  return value === 'archived' ? 'archived' : 'active'
}

function normalizeKey(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function toOptionalDate(value: unknown): Date | undefined {
  if (value instanceof Timestamp) {
    return value.toDate()
  }
  if (value instanceof Date) {
    return value
  }
  return undefined
}

function readOptionalString(data: Record<string, unknown>, key: string): string | undefined {
  if (typeof data[key] !== 'string') {
    return undefined
  }
  const value = data[key].trim()
  return value.length > 0 ? value : undefined
}

function readLikesCount(data: Record<string, unknown>): number {
  return typeof data['likes_count'] === 'number' ? Math.max(0, data['likes_count']) : 0
}

function isAdminUser(user: UserProfile): boolean {
  return user.admin === true || user.isAdmin === true
}

function assertCanManageSuggestion(
  currentUser: UserProfile,
  submittedByUid: string,
  action: string,
): void {
  if (!currentUser.uid) {
    throw new CoreError({
      cause: CoreErrorCause.Forbidden,
      context: `No authenticated user available for ${action}.`,
    })
  }

  if (isAdminUser(currentUser) || currentUser.uid === submittedByUid) {
    return
  }

  throw new CoreError({
    cause: CoreErrorCause.PermissionDenied,
    context: `Current user is not allowed to ${action}.`,
  })
}

function getSuggestionsCollection() {
  if (!firestore) {
    throw new CoreError({
      cause: CoreErrorCause.Unknown,
      context: 'Firestore is not initialized.',
    })
  }

  return collection(firestore, SUGGESTIONS_COLLECTION)
}

function getSuggestionDoc(suggestionId: string) {
  if (!firestore) {
    throw new CoreError({
      cause: CoreErrorCause.Unknown,
      context: 'Firestore is not initialized.',
    })
  }

  return doc(firestore, SUGGESTIONS_COLLECTION, suggestionId)
}

function getUserDoc(userId: string) {
  if (!firestore) {
    throw new CoreError({
      cause: CoreErrorCause.Unknown,
      context: 'Firestore is not initialized.',
    })
  }

  return doc(firestore, USERS_COLLECTION, userId)
}

function getSuggestionLikesCollection(suggestionId: string) {
  return collection(getSuggestionDoc(suggestionId), SUGGESTION_LIKES_SUBCOLLECTION)
}

function getSuggestionLikeDoc(suggestionId: string, userId: string) {
  return doc(getSuggestionLikesCollection(suggestionId), userId)
}

async function deleteLikesCollectionInBatches(
  likesCollectionRef: ReturnType<typeof getSuggestionLikesCollection>,
): Promise<void> {
  if (!firestore) {
    throw new CoreError({
      cause: CoreErrorCause.Unknown,
      context: 'Firestore is not initialized.',
    })
  }

  const deleteBatchSize = 400
  while (true) {
    const likesSnapshot = await getDocs(query(likesCollectionRef, limit(deleteBatchSize)))
    if (likesSnapshot.empty) {
      break
    }

    const batch = writeBatch(firestore)
    likesSnapshot.docs.forEach((likeDoc) => {
      batch.delete(likeDoc.ref)
    })
    await batch.commit()

    if (likesSnapshot.size < deleteBatchSize) {
      break
    }
  }
}

function toWebinarSuggestion(
  suggestionId: string,
  data: Record<string, unknown>,
  likedSuggestionIds: Set<string>,
): WebinarSuggestion {
  return {
    uid: suggestionId,
    subject: readOptionalString(data, 'subject') ?? '',
    description: readOptionalString(data, 'description') ?? '',
    adminReplyLabel: readOptionalString(data, 'admin_reply_label'),
    submittedOn: toOptionalDate(data['submitted_on']) ?? new Date(0),
    submittedByUid: readOptionalString(data, 'submitted_by_uid') ?? '',
    submittedByName: readOptionalString(data, 'submitted_by_name'),
    likesCount: readLikesCount(data),
    likedByCurrentUser: likedSuggestionIds.has(suggestionId),
    status: toSuggestionStatus(data['status']),
  }
}

function validateRequiredText(value: string, fieldLabel: string): string {
  const normalized = value.trim()
  if (normalized.length === 0) {
    throw new CoreError({
      cause: CoreErrorCause.Unknown,
      context: `${fieldLabel} is required.`,
    })
  }
  return normalized
}

function validateRequiredBoundedText(
  value: string,
  fieldLabel: string,
  maxLength: number,
): string {
  const normalized = validateRequiredText(value, fieldLabel)
  if (normalized.length > maxLength) {
    throw new CoreError({
      cause: CoreErrorCause.Unknown,
      context: `${fieldLabel} must be at most ${maxLength} characters.`,
    })
  }
  return normalized
}

function validateOptionalBoundedText(
  value: string | undefined,
  fieldLabel: string,
  maxLength: number,
): string | undefined {
  if (value === undefined) {
    return undefined
  }
  const normalized = value.trim()
  if (!normalized) {
    return undefined
  }
  if (normalized.length > maxLength) {
    throw new CoreError({
      cause: CoreErrorCause.Unknown,
      context: `${fieldLabel} must be at most ${maxLength} characters.`,
    })
  }
  return normalized
}

export class WebinarSuggestionsService {
  private static instance: WebinarSuggestionsService

  private constructor() {}

  public static getInstance(): WebinarSuggestionsService {
    if (!WebinarSuggestionsService.instance) {
      WebinarSuggestionsService.instance = new WebinarSuggestionsService()
    }
    return WebinarSuggestionsService.instance
  }

  private async loadLikedSuggestionIdsForUser(userId: string): Promise<Set<string>> {
    if (!firestore) {
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Firestore is not initialized.',
      })
    }

    const likesQuery = query(
      collectionGroup(firestore, SUGGESTION_LIKES_SUBCOLLECTION),
      where('suggestion_type', '==', WEBINAR_SUGGESTION_TYPE),
      where('user_uid', '==', userId),
    )

    const likesSnapshot = await getDocs(likesQuery)
    const likedSuggestionIds = likesSnapshot.docs
      .map((item) => {
        const data = item.data() as Record<string, unknown>
        return typeof data['suggestion_id'] === 'string' ? data['suggestion_id'] : null
      })
      .filter((suggestionId): suggestionId is string => Boolean(suggestionId))

    return new Set(likedSuggestionIds)
  }

  private async loadLikedSuggestionIdsByDirectReads(
    suggestionIds: string[],
    userId: string,
  ): Promise<Set<string>> {
    if (suggestionIds.length === 0) {
      return new Set<string>()
    }

    const likedSuggestionIds = await Promise.all(
      suggestionIds.map(async (suggestionId) => {
        const likeSnapshot = await getDoc(getSuggestionLikeDoc(suggestionId, userId))
        return likeSnapshot.exists() ? suggestionId : null
      }),
    )

    return new Set(
      likedSuggestionIds.filter(
        (suggestionId): suggestionId is string => Boolean(suggestionId),
      ),
    )
  }

  public async createWebinarSuggestion(
    input: CreateWebinarSuggestionInput,
    currentUser: UserProfile,
  ): Promise<string> {
    if (!currentUser.uid) {
      throw new CoreError({
        cause: CoreErrorCause.Forbidden,
        context: 'No authenticated user available for webinar suggestions.',
      })
    }

    const subject = validateRequiredBoundedText(input.subject, 'Subject', SUBJECT_MAX_LENGTH)
    const description = validateRequiredBoundedText(
      input.description,
      'Description',
      DESCRIPTION_MAX_LENGTH,
    )
    const adminReplyLabel = validateOptionalBoundedText(
      input.adminReplyLabel,
      'Admin reply label',
      ADMIN_REPLY_LABEL_MAX_LENGTH,
    )

    try {
      const payload = {
        subject,
        description,
        subject_key: normalizeKey(subject),
        submitted_on: new Date(),
        submitted_by_uid: currentUser.uid,
        submitted_by_name: currentUser.profile_slug || currentUser.fullName || null,
        submitted_by_ref: getUserDoc(currentUser.uid),
        likes_count: 0,
        admin_reply_label: adminReplyLabel ?? null,
        status: 'active' as WebinarSuggestionStatus,
      }

      const createdDoc = await addDoc(getSuggestionsCollection(), payload)
      return createdDoc.id
    } catch (error) {
      throw WebinarSuggestionsErrorConverter.domainModel(error)
    }
  }

  public async fetchWebinarSuggestions(
    currentUserId?: string,
    maxCount?: number,
  ): Promise<WebinarSuggestion[]> {
    try {
      const suggestionsQuery =
        typeof maxCount === 'number' && maxCount > 0
          ? query(
              getSuggestionsCollection(),
              orderBy('likes_count', 'desc'),
              orderBy('submitted_on', 'desc'),
              limit(maxCount),
            )
          : query(
              getSuggestionsCollection(),
              orderBy('likes_count', 'desc'),
              orderBy('submitted_on', 'desc'),
            )
      const suggestionsSnapshot = await getDocs(suggestionsQuery)
      let likedSuggestionIds = new Set<string>()
      if (currentUserId) {
        try {
          likedSuggestionIds = await this.loadLikedSuggestionIdsForUser(currentUserId)
        } catch (likesLookupError) {
          const mappedError =
            WebinarSuggestionsErrorConverter.domainModel(likesLookupError)
          if (mappedError.cause !== CoreErrorCause.PermissionDenied) {
            throw likesLookupError
          }

          try {
            likedSuggestionIds = await this.loadLikedSuggestionIdsByDirectReads(
              suggestionsSnapshot.docs.map((item) => item.id),
              currentUserId,
            )
          } catch {
            likedSuggestionIds = new Set<string>()
          }
        }
      }

      return suggestionsSnapshot.docs.map((item) =>
        toWebinarSuggestion(
          item.id,
          item.data() as Record<string, unknown>,
          likedSuggestionIds,
        ),
      )
    } catch (error) {
      throw WebinarSuggestionsErrorConverter.domainModel(error)
    }
  }

  public async fetchWebinarSuggestion(
    suggestionId: string,
    currentUserId?: string,
  ): Promise<WebinarSuggestion> {
    try {
      const suggestionSnapshot = await getDoc(getSuggestionDoc(suggestionId))
      if (!suggestionSnapshot.exists()) {
        throw new CoreError({
          cause: CoreErrorCause.DataNotFound,
          context: `Webinar suggestion "${suggestionId}" was not found.`,
        })
      }

      let likedSuggestionIds = new Set<string>()
      if (currentUserId) {
        try {
          const likesSnapshot = await getDoc(
            getSuggestionLikeDoc(suggestionId, currentUserId),
          )
          if (likesSnapshot.exists()) {
            likedSuggestionIds.add(suggestionId)
          }
        } catch (likesLookupError) {
          const mappedError =
            WebinarSuggestionsErrorConverter.domainModel(likesLookupError)
          if (mappedError.cause !== CoreErrorCause.PermissionDenied) {
            throw likesLookupError
          }
        }
      }

      return toWebinarSuggestion(
        suggestionSnapshot.id,
        suggestionSnapshot.data() as Record<string, unknown>,
        likedSuggestionIds,
      )
    } catch (error) {
      throw WebinarSuggestionsErrorConverter.domainModel(error)
    }
  }

  public async toggleWebinarSuggestionLike(
    suggestionId: string,
    userId: string,
  ): Promise<boolean> {
    if (!firestore) {
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Firestore is not initialized.',
      })
    }

    try {
      return await runTransaction(firestore, async (transaction) => {
        const suggestionDocRef = getSuggestionDoc(suggestionId)
        const likeDocRef = getSuggestionLikeDoc(suggestionId, userId)

        const suggestionSnapshot = await transaction.get(suggestionDocRef)
        if (!suggestionSnapshot.exists()) {
          throw new CoreError({
            cause: CoreErrorCause.DataNotFound,
            context: `Webinar suggestion "${suggestionId}" was not found.`,
          })
        }

        const likeSnapshot = await transaction.get(likeDocRef)
        const suggestionData = suggestionSnapshot.data() as Record<string, unknown>
        const currentLikes =
          typeof suggestionData['likes_count'] === 'number'
            ? Math.max(0, suggestionData['likes_count'])
            : 0

        if (likeSnapshot.exists()) {
          transaction.delete(likeDocRef)
          transaction.update(suggestionDocRef, {
            likes_count: Math.max(0, currentLikes - 1),
          })
          return false
        }

        transaction.set(likeDocRef, {
          user_uid: userId,
          suggestion_id: suggestionId,
          suggestion_type: WEBINAR_SUGGESTION_TYPE,
          liked_on: new Date(),
        })
        transaction.update(suggestionDocRef, {
          likes_count: currentLikes + 1,
        })
        return true
      })
    } catch (error) {
      throw WebinarSuggestionsErrorConverter.domainModel(error)
    }
  }

  public async updateWebinarSuggestion(
    suggestionId: string,
    patch: UpdateWebinarSuggestionPatch,
    currentUser: UserProfile,
  ): Promise<void> {
    try {
      const suggestionSnapshot = await getDoc(getSuggestionDoc(suggestionId))
      if (!suggestionSnapshot.exists()) {
        throw new CoreError({
          cause: CoreErrorCause.DataNotFound,
          context: `Webinar suggestion "${suggestionId}" was not found.`,
        })
      }

      const suggestionData = suggestionSnapshot.data() as Record<string, unknown>
      const submittedByUid = readOptionalString(suggestionData, 'submitted_by_uid') ?? ''
      assertCanManageSuggestion(currentUser, submittedByUid, 'manage webinar suggestions')

      const payload: Record<string, unknown> = {}

      if (patch.subject !== undefined) {
        const subject = validateRequiredBoundedText(
          patch.subject,
          'Subject',
          SUBJECT_MAX_LENGTH,
        )
        payload['subject'] = subject
        payload['subject_key'] = normalizeKey(subject)
      }

      if (patch.description !== undefined) {
        payload['description'] = validateRequiredBoundedText(
          patch.description,
          'Description',
          DESCRIPTION_MAX_LENGTH,
        )
      }

      if (patch.adminReplyLabel !== undefined) {
        payload['admin_reply_label'] = validateOptionalBoundedText(
          patch.adminReplyLabel,
          'Admin reply label',
          ADMIN_REPLY_LABEL_MAX_LENGTH,
        ) ?? null
      }

      if (patch.status !== undefined) {
        payload['status'] = patch.status
      }

      if (Object.keys(payload).length === 0) {
        return
      }

      await updateDoc(getSuggestionDoc(suggestionId), payload)
    } catch (error) {
      throw WebinarSuggestionsErrorConverter.domainModel(error)
    }
  }

  public async deleteWebinarSuggestion(
    suggestionId: string,
    currentUser: UserProfile,
  ): Promise<void> {
    try {
      const suggestionSnapshot = await getDoc(getSuggestionDoc(suggestionId))
      if (!suggestionSnapshot.exists()) {
        throw new CoreError({
          cause: CoreErrorCause.DataNotFound,
          context: `Webinar suggestion "${suggestionId}" was not found.`,
        })
      }

      const suggestionData = suggestionSnapshot.data() as Record<string, unknown>
      const submittedByUid = readOptionalString(suggestionData, 'submitted_by_uid') ?? ''
      assertCanManageSuggestion(currentUser, submittedByUid, 'delete webinar suggestions')

      await deleteLikesCollectionInBatches(getSuggestionLikesCollection(suggestionId))
      await deleteDoc(getSuggestionDoc(suggestionId))
    } catch (error) {
      throw WebinarSuggestionsErrorConverter.domainModel(error)
    }
  }
}

const webinarSuggestionsService = WebinarSuggestionsService.getInstance()

export async function createWebinarSuggestion(
  input: CreateWebinarSuggestionInput,
  currentUser: UserProfile,
): Promise<string> {
  return webinarSuggestionsService.createWebinarSuggestion(input, currentUser)
}

export async function fetchWebinarSuggestions(
  currentUserId?: string,
  maxCount?: number,
): Promise<WebinarSuggestion[]> {
  return webinarSuggestionsService.fetchWebinarSuggestions(currentUserId, maxCount)
}

export async function fetchWebinarSuggestion(
  suggestionId: string,
  currentUserId?: string,
): Promise<WebinarSuggestion> {
  return webinarSuggestionsService.fetchWebinarSuggestion(suggestionId, currentUserId)
}

export async function toggleWebinarSuggestionLike(
  suggestionId: string,
  userId: string,
): Promise<boolean> {
  return webinarSuggestionsService.toggleWebinarSuggestionLike(suggestionId, userId)
}

export async function updateWebinarSuggestion(
  suggestionId: string,
  patch: UpdateWebinarSuggestionPatch,
  currentUser: UserProfile,
): Promise<void> {
  return webinarSuggestionsService.updateWebinarSuggestion(
    suggestionId,
    patch,
    currentUser,
  )
}

export async function deleteWebinarSuggestion(
  suggestionId: string,
  currentUser: UserProfile,
): Promise<void> {
  return webinarSuggestionsService.deleteWebinarSuggestion(suggestionId, currentUser)
}
